import { Box, Typography, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getDriveshaftToleranceClassesByMaxRotorSpeed } from "./Data";
import { useCallback, useMemo, useState } from "react";
import {
  getMassUnits,
} from "../../../../utils/helpers/tolerance/GeneralHelper";
import { useMeasureInUnits } from "../../../../hooks/useMeasureInUnits";
import {
  isPositiveConstraint,
  useConstraint,
} from "../../../../hooks/useConstraint";
import AccuracyClassSelector from "../../../../components/Widgets/Tolerance/AccuracyClassSelector/AccuracyClassSelector";
import HelpInput from "../../../../components/Inputs/HelpInput/HelpInput";
import DisbalancePerSupport from "../../../../components/Widgets/DriveshaftTolerance/DisbalancePerSupport/DisbalancePerSupport";
import PermissibleUnbalanceMassCalculation from "../../../../components/Widgets/DriveshaftTolerance/Other/PermissibleUnbalanceMassCalculation/PermissibleUnbalanceMassCalculation";
import CriticalRotationSpeed from "../../../../components/Widgets/DriveshaftTolerance/Other/CriticalRotationSpeed/CriticalRotationSpeed";
import InputStack from "../../../../components/Styled/Stack/InputStack";
import PageContainerBox from "../../../../components/Styled/Box/PageContainerBox";
export default function DriveshaftToleranceWidget() {
  const { t } = useTranslation();

  const [toleranceClass, setToleranceClass] = useState(null);
  const [supports, setSupports] = useState(2);

  const selectorData = useMemo(() => {
    return getDriveshaftToleranceClassesByMaxRotorSpeed(t, "Ω");
  }, [t]);

  const partMassUnits = useMemo(() => getMassUnits(t), [t]);

  const [
    massInKg,
    mass,
    setMass /* unitsOfMass, massUnit, handleMassUnitChange */,
  ] = useMeasureInUnits(null, partMassUnits[2]);

  const residualDisbalance = useMemo(
    () => toleranceClass?.value * massInKg,
    [toleranceClass, massInKg]
  );

  const residualDisbalanceByPlane = useMemo(
    () => residualDisbalance / supports,
    [residualDisbalance, supports]
  );

  const isDisabled = useMemo(() => {
    return !toleranceClass || toleranceClass?.value === 0;
  }, [toleranceClass]);

  const selectorDefaultLabel = useMemo(
    () =>
      t(
        "pages.driveshaftTolerance.components.toleranceClassSelector.selectorLabel"
      ),
    [t]
  );

  const handleMassChange = useConstraint(setMass, isPositiveConstraint);

  const handleSupportToggle = useCallback((event) => {
    if (+event.target.value < 2) setSupports(2);
    else if (+event.target.value > 5) setSupports(5);
    else setSupports(+event.target.value);
  }, []);

  return (
    <PageContainerBox>
      <Typography variant="h4" sx={{ my: 2 }}>
        {t("pages.driveshaftTolerance.title")}
      </Typography>
      <Typography variant="h6" gutterBottom>
        {t("pages.driveshaftTolerance.components.toleranceClassSelector.title")}
      </Typography>
      <AccuracyClassSelector
        toleranceClasses={selectorData}
        onChange={setToleranceClass}
        defaultLabel={selectorDefaultLabel}
        isSearchable={false}
        value={toleranceClass}
      />
      {/* Inputs for Frequency and Mass */}
      <Typography variant="h6" sx={{ my: 2 }}>
        {t("pages.driveshaftTolerance.components.massInput.title")}
      </Typography>
      <InputStack>
        <HelpInput
          label={`m, ${t("units.mass.kg")}`}
          type="number"
          variant="outlined"
          fullWidth
          value={mass || ""}
          onChange={handleMassChange}
          helpButtonText={t(
            "pages.driveshaftTolerance.components.massInput.helpButtonText"
          )}
          disabled={isDisabled}
        />
      </InputStack>
      <Typography variant="h6" sx={{ my: 2 }}>
        {t("pages.driveshaftTolerance.components.supportsInput.title")}
      </Typography>
      <InputStack>
        <RadioGroup
          row
          value={supports}
          onChange={handleSupportToggle}
        >
          {Array.from({ length: 4 }, (_, index) => index + 2).map((value) => (
            <FormControlLabel key={value} value={value} label={value} control={<Radio />}  />
          ))}
        </RadioGroup>
      </InputStack>
      {(mass !== null && mass !== 0) && (
        <>
          <DisbalancePerSupport
            residualDisbalance={residualDisbalance}
            residualDisbalanceByPlane={residualDisbalanceByPlane}
          />
          <Typography variant="h4" sx={{ my: 2 }}>
            {t("shared.disbalance.additionalCalculations")}
          </Typography>
          <PermissibleUnbalanceMassCalculation
            supports={supports}
            residualDisbalanceByPlane={residualDisbalanceByPlane}
          />
          <CriticalRotationSpeed isDisabled={isDisabled} />
        </>
      )}
    </PageContainerBox>
  );
}
